import React, { useMemo } from 'react';
import { Box, Button, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { FolioDrawerHeader } from '@/components/Drawers/FolioDrawer/FolioDrawerHeader';
import { useAdminConfirm } from '../../shared/AdminConfirmProvider';
import {
	useGetBrokerGroupQuery,
	useListCountriesQuery,
	useUpdateBrokerGroupMutation,
} from '../api/brokerGroupsApi';
import { getBrokerGroupValidationSchema } from '../brokerGroupValidationSchema';
import {
	useListAllFeatureFlagsQuery,
	useUpdateFeatureFlagMutation,
} from '@/admin/featureFlags/api/featureFlagsApi';
import {
	BROKER_GROUP_FIELD_LABELS,
	getBrokerGroupChanges,
	getBrokerGroupFormValues,
	getFeatureFlagChanges,
} from './brokerGroupFormValues';
import { FeatureFlagFields } from '@/admin/brokerGroups/BrokerGroupDrawer/FeatureFlagFields';
import { UserFields } from '@/admin/brokerGroups/BrokerGroupDrawer/UserFields';
import { BrokerGroupChangeList } from './BrokerGroupChangeList';
import { BrokerGroupLogo } from './BrokerGroupLogo';
import { BrokerGroupTextField } from '@/admin/brokerGroups/BrokerGroupDrawer/BrokerGroupTextField';
import { LedgerFields } from '@/admin/brokerGroups/BrokerGroupDrawer/LedgerFields';
import { LocationFields } from '@/admin/brokerGroups/BrokerGroupDrawer/LocationFields';
import { SharepointFields } from '@/admin/brokerGroups/BrokerGroupDrawer/SharepointFields';
import { AdminLastUpdated } from '@/admin/shared/AdminLastUpdated';
import FolioDrawerFormGuard from '@/components/Drawers/FolioDrawer/FolioDrawerFormGuard';
import { FolioDrawerFooter } from '@/components/Drawers/FolioDrawer/FolioDrawerFooter';

// Every field starts touched, so data that's already invalid (e.g. a timezone that doesn't
// fit the country) shows its error as soon as the drawer opens
const INITIAL_TOUCHED = Object.fromEntries(
	Object.keys(BROKER_GROUP_FIELD_LABELS).map((field) => [field, true]),
);

/**
 * Edits one broker group (getBrokerGroup, filled from the cached broker group list) with Formik and
 * getBrokerGroupValidationSchema. Save lists each change for the admin to confirm, then
 * sends only the changed fields (country, state and timezone together when any of them
 * changed) through updateBrokerGroup, then adds or removes the broker group on each feature
 * flag that changed (updateFeatureFlag), and closes. Errors the API ties to a field show
 * under it and keep the drawer open; others, and flags that fail, show a toast.
 */
export const BrokerGroupForm = ({ brokerGroupId, onClose }) => {
	const dispatch = useDispatch();
	const confirm = useAdminConfirm();
	const [updateBrokerGroup] = useUpdateBrokerGroupMutation();
	const { data: brokerGroup, isLoading } = useGetBrokerGroupQuery(brokerGroupId);
	const { data: countries, isLoading: areCountriesLoading } = useListCountriesQuery();
	const [updateFeatureFlag] = useUpdateFeatureFlagMutation();
	const { data: flags, isLoading: areFlagsLoading } = useListAllFeatureFlagsQuery();

	const validationSchema = useMemo(
		() => countries && getBrokerGroupValidationSchema(countries),
		[countries],
	);
	const initialValues = useMemo(
		() => brokerGroup && flags && getBrokerGroupFormValues(brokerGroup, flags),
		[brokerGroup, flags],
	);

	if (isLoading || areCountriesLoading || areFlagsLoading) {
		return (
			<Box display='flex' justifyContent='center' py={4}>
				<CircularProgress />
			</Box>
		);
	}
	if (!brokerGroup || !countries || !flags) {
		return (
			<>
				<FolioDrawerHeader title='Broker group' icon={<BusinessOutlinedIcon />} />
				<Typography sx={{ p: 2 }}>Could not load this broker group.</Typography>
			</>
		);
	}

	const handleSubmit = async (values, { setErrors }) => {
		const { changed, before, after, body } = getBrokerGroupChanges(
			validationSchema,
			initialValues,
			values,
		);

		const featureFlagChanges = getFeatureFlagChanges(initialValues, values);

		const confirmed = await confirm({
			title: `Update ${brokerGroup.name}?`,
			content: (
				<BrokerGroupChangeList
					changed={changed}
					before={before}
					after={after}
					featureFlagChanges={featureFlagChanges}
				/>
			),
			confirmText: 'Save',
		});
		if (!confirmed) return;

		if (Object.keys(body).length) {
			const result = await updateBrokerGroup({ id: brokerGroupId, ...body });
			if (result.error) {
				const fields = result.error.data?.fields;
				if (fields) {
					setErrors(fields);
					return;
				}
				dispatch(
					showToast({
						message: `Couldn't update ${brokerGroup.name}. Please try again.`,
						error: true,
					}),
				);
				return;
			}
		}

		// One update per flag; the broker group is saved first, so a flag that fails can be
		// retried by saving again (the broker group's own fields then don't change)
		const flagResults = await Promise.all([
			...featureFlagChanges.added.map((id) =>
				updateFeatureFlag({ id, brokerGroups: { add: [brokerGroupId] } }),
			),
			...featureFlagChanges.removed.map((id) =>
				updateFeatureFlag({ id, brokerGroups: { remove: [brokerGroupId] } }),
			),
		]);
		const failedFlags = [...featureFlagChanges.added, ...featureFlagChanges.removed]
			.filter((id, index) => flagResults[index].error)
			.map((id) => flags.find((flag) => flag.id === id)?.feature ?? id);
		if (failedFlags.length) {
			dispatch(
				showToast({
					message: `Couldn't update these feature flags: ${failedFlags.join(', ')}. Please try again.`,
					error: true,
				}),
			);
			return;
		}
		dispatch(showToast({ message: 'Changes saved successfully.', success: true }));
		onClose();
	};

	return (
		<Stack sx={{ flex: 1, minHeight: 0, height: '100%', overflow: 'hidden', pt: 1 }}>
			<FolioDrawerHeader
				title={brokerGroup.name}
				icon={<BrokerGroupLogo fullLogo={brokerGroup.fullLogo} name={brokerGroup.name} />}
			/>
			<Formik
				initialValues={initialValues}
				initialTouched={INITIAL_TOUCHED}
				validationSchema={validationSchema}
				validateOnMount
				onSubmit={handleSubmit}
			>
				{({ dirty, isValid, isSubmitting }) => (
					<Form
						style={{
							display: 'flex',
							flexDirection: 'column',
							flex: 1,
							minHeight: 0,
							width: '100%',
							height: '100%',
							overflow: 'hidden',
						}}
					>
						<Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
							<Grid container spacing={2}>
								<Grid size={12}>
									<Typography variant='form_label'>Details</Typography>
								</Grid>

								<BrokerGroupTextField name='name' />

								<LedgerFields />

								<LocationFields />
								<SharepointFields {...brokerGroup} />
								<FeatureFlagFields ledger={brokerGroup.ledger} />
								<UserFields brokerGroupId={brokerGroupId} />
							</Grid>
						</Box>
						<AdminLastUpdated {...brokerGroup} />
						<FolioDrawerFormGuard />
						<FolioDrawerFooter>
							<Button
								type='submit'
								variant='contained'
								disabled={!dirty || !isValid || isSubmitting}
							>
								Save
							</Button>
						</FolioDrawerFooter>
					</Form>
				)}
			</Formik>
		</Stack>
	);
};
